import * as React from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { deleteVirtualPlayer, getVirtualPlayer, regenerateVirtualPlayerImage, updateVirtualPlayer } from '../../Api';
import { isIsoCountryCode } from '../../constants/IsoCountries';
import { VirtualPlayerDraftDto, VirtualProviderPlayerDto } from '../../models/dtos/VirtualProviderCatalogDto';
import VirtualCountrySelect from './VirtualCountrySelect';
import VirtualPlayerSkills from './VirtualPlayerSkills';

interface VirtualPlayerDetailsDialogProps {
    player: VirtualProviderPlayerDto | null;
    sportNames: Map<number, string>;
    onClose: () => void;
    onUpdated: (player: VirtualProviderPlayerDto) => void;
    onDeleted: (playerId: string, playerName: string) => void;
}

const EMPTY_DRAFT: VirtualPlayerDraftDto = {
    name: '',
    nameRu: '',
    description: '',
    descriptionRu: '',
    originCountry: '',
    yearOfBirth: new Date().getFullYear() - 20,
    eyeColor: '',
    skinColor: '',
    faceShape: '',
    hairStyle: '',
    strength: 0.5,
    technique: 0.5,
    intelligence: 0.5,
};

const toDraft = (player: VirtualProviderPlayerDto): VirtualPlayerDraftDto => ({
    name: player.name,
    nameRu: player.nameRu ?? '',
    description: player.description ?? '',
    descriptionRu: player.descriptionRu ?? '',
    originCountry: player.originCountry,
    yearOfBirth: player.yearOfBirth,
    eyeColor: player.eyeColor,
    skinColor: player.skinColor,
    faceShape: player.faceShape,
    hairStyle: player.hairStyle,
    strength: player.strength,
    technique: player.technique,
    intelligence: player.intelligence,
});

const formatDate = (value?: string | null) => value ? new Date(value).toLocaleString() : '—';

export default function VirtualPlayerDetailsDialog(props: VirtualPlayerDetailsDialogProps) {
    const [player, setPlayer] = React.useState<VirtualProviderPlayerDto | null>(null);
    const [draft, setDraft] = React.useState<VirtualPlayerDraftDto>(EMPTY_DRAFT);
    const [editing, setEditing] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [regeneratingImage, setRegeneratingImage] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!props.player) return;
        let cancelled = false;
        setPlayer(props.player);
        setDraft(toDraft(props.player));
        setEditing(false);
        setDeleteConfirmOpen(false);
        setError('');
        setLoading(true);
        getVirtualPlayer(props.player.id)
            .then(freshPlayer => {
                if (cancelled) return;
                setPlayer(freshPlayer);
                setDraft(toDraft(freshPlayer));
            })
            .catch(reason => {
                if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load player details');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [props.player]);

    const update = <K extends keyof VirtualPlayerDraftDto>(field: K, value: VirtualPlayerDraftDto[K]) =>
        setDraft(current => ({ ...current, [field]: value }));
    const maximumBirthYear = new Date().getFullYear() - 16;
    const valid = draft.name.trim().length >= 3
        && draft.nameRu.trim().length >= 3
        && draft.description.trim().length >= 40
        && draft.descriptionRu.trim().length >= 40
        && draft.eyeColor.trim().length >= 2
        && draft.skinColor.trim().length >= 2
        && draft.faceShape.trim().length >= 2
        && draft.hairStyle.trim().length >= 2
        && [draft.strength, draft.technique, draft.intelligence].every(value => value >= 0 && value <= 1)
        && isIsoCountryCode(draft.originCountry)
        && draft.yearOfBirth >= 1940
        && draft.yearOfBirth <= maximumBirthYear;

    const save = async () => {
        if (!player || !valid) return;
        setSaving(true);
        setError('');
        try {
            const updated = await updateVirtualPlayer(player.id, draft);
            setPlayer(updated);
            setDraft(toDraft(updated));
            setEditing(false);
            props.onUpdated(updated);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not update player');
        } finally {
            setSaving(false);
        }
    };

    const currentMembership = player?.memberships.find(membership => membership.effective);

    const regenerateImage = async () => {
        if (!player) return;
        setRegeneratingImage(true);
        setError('');
        try {
            const updated = await regenerateVirtualPlayerImage(player.id);
            setPlayer(updated);
            setDraft(toDraft(updated));
            props.onUpdated(updated);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not regenerate player portrait');
        } finally {
            setRegeneratingImage(false);
        }
    };

    const deletePlayer = async () => {
        if (!player) return;
        setDeleting(true);
        setError('');
        try {
            await deleteVirtualPlayer(player.id);
            setDeleteConfirmOpen(false);
            props.onDeleted(player.id, player.name);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not delete player');
            setDeleteConfirmOpen(false);
        } finally {
            setDeleting(false);
        }
    };

    return <><Dialog open={props.player != null} onClose={saving || regeneratingImage || deleting ? undefined : props.onClose} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Edit virtual player' : 'Virtual player details'}</DialogTitle>
        <DialogContent dividers>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {loading && !player ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box> : player ? (
                editing ? <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Sport: {player.sportId == null ? '—' : props.sportNames.get(player.sportId) ?? `Sport ${player.sportId}`}
                    </Typography>
                    <TextField required label="Name (English)" value={draft.name} onChange={event => update('name', event.target.value)} />
                    <TextField required label="Name (Russian)" value={draft.nameRu} onChange={event => update('nameRu', event.target.value)} />
                    <TextField required multiline minRows={4} label="Biography (English)" value={draft.description} onChange={event => update('description', event.target.value)} />
                    <TextField required multiline minRows={4} label="Biography (Russian)" value={draft.descriptionRu} onChange={event => update('descriptionRu', event.target.value)} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <VirtualCountrySelect value={draft.originCountry} onChange={countryCode => update('originCountry', countryCode)} disabled={saving} />
                        <TextField required type="number" label="Year of birth" value={draft.yearOfBirth} onChange={event => update('yearOfBirth', Number(event.target.value))} sx={{ flex: 1 }} />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField required label="Eye color" value={draft.eyeColor} onChange={event => update('eyeColor', event.target.value)} sx={{ flex: 1 }} />
                        <TextField required label="Skin color" value={draft.skinColor} onChange={event => update('skinColor', event.target.value)} sx={{ flex: 1 }} />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField required label="Face shape" value={draft.faceShape} onChange={event => update('faceShape', event.target.value)} sx={{ flex: 1 }} />
                        <TextField required label="Hair style" value={draft.hairStyle} onChange={event => update('hairStyle', event.target.value)} sx={{ flex: 1 }} />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField required type="number" label="Strength" value={draft.strength} onChange={event => update('strength', Number(event.target.value))} inputProps={{ min: 0, max: 1, step: 0.01 }} helperText="0 to 1" sx={{ flex: 1 }} />
                        <TextField required type="number" label="Technique" value={draft.technique} onChange={event => update('technique', Number(event.target.value))} inputProps={{ min: 0, max: 1, step: 0.01 }} helperText="0 to 1" sx={{ flex: 1 }} />
                        <TextField required type="number" label="Intelligence" value={draft.intelligence} onChange={event => update('intelligence', Number(event.target.value))} inputProps={{ min: 0, max: 1, step: 0.01 }} helperText="0 to 1" sx={{ flex: 1 }} />
                    </Stack>
                </Stack> : <Stack spacing={3}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                        <Avatar src={player.imageUrl ?? undefined} sx={{ width: 72, height: 72 }}>{player.name.slice(0, 1)}</Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h5">{player.name}</Typography>
                            {player.nameRu ? <Typography color="text.secondary">{player.nameRu}</Typography> : null}
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                <Chip label={player.sportId == null ? 'No sport' : props.sportNames.get(player.sportId) ?? `Sport ${player.sportId}`} />
                                <Chip label={`${player.originCountry} · ${player.yearOfBirth}`} variant="outlined" />
                                <Chip label={`${player.eyeColor} eyes`} variant="outlined" />
                                <Chip
                                    label={currentMembership ? `${currentMembership.team.name} · ${currentMembership.lineupType.replace(/_/g, ' ')}` : 'Unassigned'}
                                    color={currentMembership ? 'primary' : 'default'}
                                    variant="outlined"
                                />
                            </Stack>
                        </Box>
                    </Stack>
                    <Divider />
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary">Appearance</Typography>
                        <Typography sx={{ mt: 0.5 }}>
                            Skin: {player.skinColor} · Face: {player.faceShape} · Hair: {player.hairStyle}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary">Characteristics</Typography>
                        <VirtualPlayerSkills player={player} variant="labeled" />
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary">Biography (English)</Typography>
                        <Typography sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{player.description || '—'}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary">Biography (Russian)</Typography>
                        <Typography sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{player.descriptionRu || '—'}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Team membership history</Typography>
                        {player.memberships.length === 0 ? <Typography color="text.secondary">No team memberships.</Typography> : (
                            <Stack spacing={1}>
                                {player.memberships.map(membership => <Box key={membership.id} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                        <Typography sx={{ flex: 1 }}>{membership.team.name}</Typography>
                                        <Chip size="small" label={membership.lineupType.replace(/_/g, ' ')} />
                                        {membership.effective ? <Chip size="small" color="success" label="Current" /> : null}
                                        {!membership.active ? <Chip size="small" label="Inactive" /> : null}
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDate(membership.validFrom)} – {formatDate(membership.validTo)}
                                    </Typography>
                                </Box>)}
                            </Stack>
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Created {formatDate(player.createdAt)} · Updated {formatDate(player.updatedAt)} · ID {player.id}
                    </Typography>
                </Stack>
            ) : null}
        </DialogContent>
        <DialogActions>
            {editing ? <>
                <Button onClick={() => { if (player) setDraft(toDraft(player)); setEditing(false); }} disabled={saving}>Cancel editing</Button>
                <Button variant="contained" onClick={save} disabled={!valid || saving}>{saving ? <CircularProgress size={20} /> : 'Save'}</Button>
            </> : <>
                <Button color="error" onClick={() => setDeleteConfirmOpen(true)} disabled={!player || loading || regeneratingImage || deleting}>Delete player</Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={props.onClose} disabled={regeneratingImage || deleting}>Close</Button>
                <Button variant="outlined" onClick={regenerateImage} disabled={!player || loading || regeneratingImage}>
                    {regeneratingImage ? <CircularProgress size={20} /> : player?.imageUrl ? 'Regenerate portrait' : 'Generate portrait'}
                </Button>
                <Button variant="contained" onClick={() => setEditing(true)} disabled={!player || loading || regeneratingImage}>Edit player</Button>
            </>}
        </DialogActions>
    </Dialog>
        <Dialog open={deleteConfirmOpen} onClose={deleting ? undefined : () => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Delete virtual player?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {player?.name ?? 'This player'} will be permanently deleted and removed from every team membership. This cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
                <Button color="error" variant="contained" onClick={deletePlayer} disabled={deleting}>
                    {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete player'}
                </Button>
            </DialogActions>
        </Dialog>
    </>;
}
